/**
 * Per-table "snap to the state you just arrived at" window.
 *
 * Switching into a table remounts that table's whole subtree (the providers in
 * pages/Game.tsx and MultiTableGrid are keyed by gameID). Every mount-time
 * animation therefore re-plays state that already happened while the player was
 * looking at a different table: hole cards deal in, the board flips, bet pills
 * pop, chips fly, the pot counts up. On a phone running several tables that
 * burst of GSAP work lands in the same frames as the switch itself, which is
 * what makes a switch stutter.
 *
 * While a table is "snapping", the animation call sites place their elements at
 * the FINAL state instead of tweening to it (see animations/snapTween). Nothing
 * is disabled: the window closes as soon as the arrival state has painted, and
 * every action that arrives after that animates exactly as before.
 *
 * Two things open a window:
 *   • ARRIVAL — hooks/useTableSnap's useSnapOnArrival takes a hold when a table
 *     mounts and releases it once that table's current state has painted. This
 *     covers every way a table becomes the viewed table (grid tile, table
 *     switcher, swipe, return from lobby, deep link, post-login restore), since
 *     they all mount the table subtree.
 *   • RESYNC — a reconnect / foreground re-subscribe re-hydrates tables the
 *     player is ALREADY viewing without remounting anything. ConnectionManager
 *     already flags that burst via utils/tableResyncState (the seat-rotation
 *     layer consumes the same signal), so it is folded in here rather than
 *     adding a second reconnect call site.
 *
 * Holds are counted, not boolean: on desktop the same gameID can be mounted
 * twice at once (the single-table view plus its hidden grid cell), and one of
 * them unmounting must not release the other's window. A deadline backs the
 * count so a table that never hydrates can't wedge the flag on.
 *
 * Read imperatively (no React re-render / context plumbing) at each animation
 * decision point — the same shape as utils/tableResyncState.
 */

import { isTableResyncing } from 'utils/tableResyncState';

/**
 * Ceiling on a single arrival window. Comfortably longer than a slow restore
 * (subscribe → first snapshot) so it never cuts a genuine hydration short,
 * short enough that a table which never hydrates cannot suppress animations for
 * the rest of the session.
 */
export const SNAP_MAX_MS = 20000;

/** gameID → number of live holds (mounted table views awaiting their state). */
const holdsByTable = new Map<string, number>();
/** gameID → hard expiry for the current window. */
const deadlineByTable = new Map<string, number>();

/**
 * Take a hold: this table is arriving and its state must be snapped, not
 * animated. Every call must be paired with exactly one endTableSnap.
 */
export function beginTableSnap(gameID: string, maxMs: number = SNAP_MAX_MS): void {
  if (!gameID) return;
  const key = String(gameID);
  holdsByTable.set(key, (holdsByTable.get(key) ?? 0) + 1);
  deadlineByTable.set(key, Date.now() + maxMs);
}

/** Release one hold. The window closes when the last hold is released. */
export function endTableSnap(gameID: string): void {
  if (!gameID) return;
  const key = String(gameID);
  const remaining = (holdsByTable.get(key) ?? 0) - 1;
  if (remaining > 0) {
    holdsByTable.set(key, remaining);
    return;
  }
  holdsByTable.delete(key);
  deadlineByTable.delete(key);
}

/**
 * True while this table's incoming state should be applied instantly (no
 * animation) — either it is still arriving, or a background resync burst is
 * landing on tables the player is already viewing.
 */
export function isTableSnapping(gameID: string): boolean {
  // Per table: a resync of a DIFFERENT table says nothing about this one, and
  // treating it as if it did froze every felt's animation whenever one table was
  // repaired (see utils/tableResyncState).
  if (isTableResyncing(gameID)) return true;
  if (!gameID) return false;
  const key = String(gameID);
  if ((holdsByTable.get(key) ?? 0) <= 0) return false;
  const deadline = deadlineByTable.get(key) ?? 0;
  if (Date.now() >= deadline) {
    // Expired — drop the stuck window so live play animates normally again.
    holdsByTable.delete(key);
    deadlineByTable.delete(key);
    return false;
  }
  return true;
}

/** Test-only reset of the module-level windows. */
export function __resetTableSnap(): void {
  holdsByTable.clear();
  deadlineByTable.clear();
}
